<template>
  <widget-dropdown-search-select
    v-model:search-query="searchQuery"
    class="widget-field-phone-country"
    :options="options"
    :readonly="readonly"
    @select="setCountryCode"
    @update:dropdown="toggle"
    @hide="$emit('update:model-value', countryCode)"
  >
    <template v-slot:selected-item>
      <button
        class="widget-field-phone-country__button"
        :class="{
          'widget-field-phone-country__button--focused': focused,
        }"
      >
        <widget-flag :country-code="modelValue" />
        <widget-icon
          class="ml-2"
          name="keyboard_arrow_down"
          :rotate="expanded ? 180 : 0"
        />
      </button>
    </template>

    <template v-slot:option="{ option }">
      <widget-option-country-with-code :option="option" />
    </template>
  </widget-dropdown-search-select>
</template>

<script lang="ts">
import WidgetIcon from '@widgets/shared/components/WidgetIcon.vue'
import WidgetDropdownSearchSelect from '@widgets/shared/components/dropdown/WidgetDropdownSearchSelect.vue'
import WidgetFlag from '@widgets/shared/components/WidgetFlag.vue'
import WidgetOptionCountryWithCode from '@widgets/shared/components/WidgetOptionCountryWithCode.vue'
import { countryPhoneService } from '@widgets/shared/services/countryPhoneService'
import { defineComponent, ref, type PropType, computed, watch } from '@/composition/vue/compositionApi'
import type { TmCountryCode } from '@/services/types'

export default defineComponent({
  components: {
    WidgetOptionCountryWithCode,
    WidgetFlag,
    WidgetDropdownSearchSelect,
    WidgetIcon,
  },
  props: {
    modelValue: {
      type: String as PropType<TmCountryCode>,
      required: true,
    },
    readonly: {
      type: Boolean,
    },
    availableCountries: {
      type: Array as PropType<TmCountryCode[]>,
      default: () => [],
    },
    focused: {
      type: Boolean,
    },
  },
  emits: {
    'update:model-value': (countryCode: TmCountryCode) => true,
    toggle: (value: boolean) => true,
  },
  setup(props, { emit }) {
    const expanded = ref(false)
    const toggle = (value: boolean) => {
      expanded.value = value
      emit('toggle', value)
    }

    const countryCode = ref(props.modelValue)
    const setCountryCode = (value: string) => {
      countryCode.value = value as TmCountryCode
    }
    watch(
      () => props.modelValue,
      () => {
        countryCode.value = props.modelValue
      },
    )

    const searchQuery = ref('')
    const countryPhones = countryPhoneService.getCountryPhones()
    const availableCountries = computed(() => {
      if (!props.availableCountries.length) return countryPhones
      return countryPhones.filter(({ value }) => props.availableCountries.includes(value))
    })
    const options = computed(() => {
      const query = searchQuery.value.toLowerCase().trim()
      if (!query) return availableCountries.value
      return availableCountries.value.filter((item) => item.searchString.toLowerCase().includes(query))
    })

    return {
      expanded,
      countryCode,
      searchQuery,
      options,
      toggle,
      setCountryCode,
    }
  },
})
</script>

<style lang="scss" scoped>
.widget-field-phone-country {
  &__button {
    display: flex;
    align-items: center;
    width: 100%;
    height: 40px;
    padding-left: 12px;
    padding-right: 5px;
    border: none;
    border-right: 1px solid $input;
    background: $shade;
    transition: background $transition-300ms-ease-in-out;
    cursor: pointer;
    border-radius: $border-radius 0 0 $border-radius;

    &--focused {
      background: $shade;
      border-color: $input;
    }
  }
}
</style>
